import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

// Drop-in replacement for react-scroll-up (unmaintained, no React 19 peer
// range): same props, fixed bottom-right placement, opacity/visibility fade,
// and a 250 ms easeOutCubic scroll to the top that a wheel/touch interrupts.
interface ScrollToTopProps {
  showUnder: number;
  children?: ReactNode;
  duration?: number;
  style?: CSSProperties;
}

const BASE_STYLE: CSSProperties = {
  position: "fixed",
  bottom: 50,
  right: 30,
  cursor: "pointer",
  transitionProperty: "opacity, visibility",
  transitionDuration: "0.2s",
  transitionTimingFunction: "linear",
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export default function ScrollToTop({ showUnder, children, duration = 250, style }: ScrollToTopProps) {
  const [show, setShow] = useState(false);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const update = () => setShow(window.pageYOffset > showUnder);
    const stop = () => {
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
      rafId.current = null;
    };
    update();
    window.addEventListener("scroll", update);
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    return () => {
      stop();
      window.removeEventListener("scroll", update);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
    };
  }, [showUnder]);

  const scrollUp = () => {
    const start = window.pageYOffset;
    const startTime = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      window.scrollTo(0, start * (1 - easeOutCubic(t)));
      rafId.current = t < 1 && window.pageYOffset > 0 ? requestAnimationFrame(step) : null;
    };
    rafId.current = requestAnimationFrame(step);
  };

  return (
    <div
      role="button"
      aria-label="Back to top"
      tabIndex={show ? 0 : -1}
      onClick={scrollUp}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          scrollUp();
        }
      }}
      style={{ ...BASE_STYLE, ...style, opacity: show ? 1 : 0, visibility: show ? "visible" : "hidden" }}
    >
      {children}
    </div>
  );
}
